/**
 * API Error Utilities
 * Transform Axios errors into typed API errors
 */
import type { AxiosError } from 'axios';
import type { ApiError, ApiErrorCode } from '@/types/api';

interface BackendErrorResponse {
  error?: string;
  message?: string;
  details?: Record<string, string[]>;
  errorCode?: string;
  traceId?: string;
}

interface ValidationProblemDetails {
  title: string;
  errors: Record<string, string[]>;
  errorCode?: string;
  traceId?: string;
  status?: number;
}

/**
 * Type guard to check if response data matches BackendErrorResponse shape
 */
function isBackendErrorResponse(data: unknown): data is BackendErrorResponse {
  if (!data || typeof data !== 'object') return false;
  const response = data as Record<string, unknown>;
  return (
    (typeof response.error === 'string' || response.error === undefined) &&
    (typeof response.message === 'string' || response.message === undefined)
  );
}

function isValidationProblemDetails(
  data: unknown
): data is ValidationProblemDetails {
  if (!data || typeof data !== 'object') return false;
  const response = data as Record<string, unknown>;
  return (
    typeof response.title === 'string' &&
    typeof response.errors === 'object' &&
    response.errors !== null
  );
}

/**
 * Transform Axios error into typed ApiError
 */
export function transformApiError(error: AxiosError<unknown>): ApiError {
  if (!error.response) {
    return {
      code: 'NETWORK_ERROR',
      message: 'Network error. Please check your connection.',
    };
  }

  const { status, data } = error.response;

  // Check ValidationProblemDetails first (has `errors` + `title`, e.g. from FluentValidation)
  if (isValidationProblemDetails(data)) {
    const errorCode = mapStatusToCode(status);
    return {
      code: errorCode,
      message: data.title || getDefaultMessage(errorCode),
      details: data.errors,
      traceId: data.traceId,
      status,
      backendErrorCode: data.errorCode,
    };
  }

  const backendData = isBackendErrorResponse(data) ? data : undefined;
  const errorCode = mapStatusToCode(status, backendData?.error);

  return {
    code: errorCode,
    message: backendData?.message ?? getDefaultMessage(errorCode),
    details: backendData?.details,
    traceId: backendData?.traceId,
    status,
    backendErrorCode: backendData?.errorCode,
  };
}

function mapStatusToCode(status: number, backendError?: string): ApiErrorCode {
  // Check for specific backend errors first
  if (backendError === 'LICENSE_BLOCKED') return 'LICENSE_BLOCKED';
  if (backendError === 'LICENSE_EXPIRED') return 'LICENSE_EXPIRED';
  if (backendError === 'RATE_LIMITED') return 'RATE_LIMITED';

  switch (status) {
    case 400:
      return 'VALIDATION_ERROR';
    case 401:
      return 'UNAUTHORIZED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 429:
      return 'RATE_LIMITED';
    case 500:
      return 'INTERNAL_ERROR';
    case 503:
      return 'SERVICE_UNAVAILABLE';
    default:
      return 'UNKNOWN_ERROR';
  }
}

function getDefaultMessage(code: ApiErrorCode): string {
  const messages: Record<ApiErrorCode, string> = {
    VALIDATION_ERROR: 'Invalid data provided.',
    NOT_FOUND: 'Resource not found.',
    UNAUTHORIZED: 'Please log in to continue.',
    FORBIDDEN: 'You do not have permission.',
    CONFLICT: 'Resource conflict occurred.',
    INTERNAL_ERROR: 'Server error. Please try again.',
    NETWORK_ERROR: 'Network error. Please check your connection.',
    LICENSE_BLOCKED: 'Your license has been blocked.',
    LICENSE_EXPIRED: 'Your license has expired.',
    RATE_LIMITED: 'Too many requests. Please try again later.',
    SERVICE_UNAVAILABLE: 'Service temporarily unavailable.',
    UNKNOWN_ERROR: 'An unexpected error occurred.',
  };
  return messages[code];
}
